/**
 * AlumniMentorsTab — Firm Portal tab. Firm owner nominates 2-3
 * alumni per university with name / role / grad year / LinkedIn /
 * private contact_email + mentor note.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import UniversityAutocomplete from "@/components/app/UniversityAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GraduationCap, Loader2, Plus, Trash2, Linkedin } from "lucide-react";


export default function AlumniMentorsTab({ firm }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uni, setUni] = useState(null);
  const [form, setForm] = useState({
    alumni_name: "", role: "", grad_year: "", linkedin_url: "",
    mentor_note: "", contact_email: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/firm-portal/${firm.slug}/alumni-mentors`);
      setRows(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [firm.slug]);

  const save = async () => {
    if (!uni?.id || !form.alumni_name.trim() || !form.role.trim() || !form.contact_email.trim()) {
      toast.error("Uni, name, role and contact email are required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/firm-portal/${firm.slug}/alumni-mentors`, {
        university_id: uni.id,
        alumni_name: form.alumni_name.trim(),
        role: form.role.trim(),
        grad_year: form.grad_year ? Number(form.grad_year) : null,
        linkedin_url: form.linkedin_url.trim() || null,
        mentor_note: form.mentor_note.trim(),
        contact_email: form.contact_email.trim(),
      });
      toast.success("Mentor added");
      setForm({ alumni_name: "", role: "", grad_year: "", linkedin_url: "", mentor_note: "", contact_email: "" });
      setUni(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/firm-portal/${firm.slug}/alumni-mentors/${id}`);
      setRows((r) => r.filter(x => x.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="firm-portal-alumni-tab">
      <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/60">
        <div className="flex items-center gap-2 text-emerald-800">
          <GraduationCap className="h-4 w-4" />
          <span className="text-[10px] font-underground-sc tracking-widest">Alumni Mentor Match</span>
        </div>
        <p className="mt-1 text-sm text-stone-700">
          Nominate up to 3 alumni per university. Students see the
          card publicly (name, role, LinkedIn, mentor note). The
          contact email you list stays private — intro requests are
          delivered via a Resend magic-link with the student's Talent
          Profile summary attached.
        </p>
      </div>

      <section
        className="p-4 rounded-xl border border-stone-200 bg-white space-y-3"
        data-testid="firm-portal-alumni-form"
      >
        <div>
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">University</div>
          <UniversityAutocomplete
            value={uni}
            onSelect={setUni}
            testIdPrefix="firm-portal-alumni-uni-ac"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input
            placeholder="Alumni full name"
            value={form.alumni_name}
            onChange={(e) => setForm({ ...form, alumni_name: e.target.value })}
            data-testid="firm-portal-alumni-name"
          />
          <Input
            placeholder="Role (e.g. Associate, TMT)"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="firm-portal-alumni-role"
          />
          <Input
            type="number"
            min={1970}
            max={2030}
            placeholder="Grad year"
            value={form.grad_year}
            onChange={(e) => setForm({ ...form, grad_year: e.target.value })}
            data-testid="firm-portal-alumni-grad"
          />
          <Input
            placeholder="LinkedIn (public — https://linkedin.com/in/…)"
            value={form.linkedin_url}
            onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
            data-testid="firm-portal-alumni-linkedin"
          />
        </div>
        <Input
          placeholder="Private contact email (for intro requests)"
          value={form.contact_email}
          onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          data-testid="firm-portal-alumni-email"
        />
        <Textarea
          rows={2}
          maxLength={240}
          placeholder="One-line mentor note shown to students"
          value={form.mentor_note}
          onChange={(e) => setForm({ ...form, mentor_note: e.target.value })}
          data-testid="firm-portal-alumni-note"
        />
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#4C1D95] hover:bg-[#3B0764] text-white"
          data-testid="firm-portal-alumni-save"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          Add mentor
        </Button>
      </section>

      <section data-testid="firm-portal-alumni-list">
        <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-2">
          Nominated mentors ({rows.length})
        </div>
        {loading ? (
          <div className="text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center border border-dashed border-stone-300 rounded-xl text-stone-500 text-sm">
            No mentors nominated yet.
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-2">
            {rows.map((m) => (
              <li
                key={m.id}
                className="p-3 rounded-xl border border-stone-200 bg-white"
                data-testid={`firm-portal-alumni-row-${m.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-stone-900 text-sm truncate">{m.alumni_name}</div>
                    <div className="text-[11px] font-mono text-stone-500">
                      {m.role}{m.grad_year ? ` · '${String(m.grad_year).slice(-2)}` : ""} · {m.university_id}
                    </div>
                    <div className="text-[10px] font-mono text-stone-400 truncate">
                      Private: {m.contact_email}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => remove(m.id)} data-testid={`firm-portal-alumni-delete-${m.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {m.linkedin_url && (
                  <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer"
                     className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#0A66C2] hover:underline">
                    <Linkedin className="h-3 w-3" /> LinkedIn
                  </a>
                )}
                {m.mentor_note && <p className="mt-1 text-[12px] text-stone-600">{m.mentor_note}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
